import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Loader2, MapPin, Mail, Phone, Building2, CheckCircle2, XCircle, Star,
  ShieldCheck, Sparkles, ArrowRight, Sun, Moon, IndianRupee, FileCheck, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { VO_COMPARISON_FEATURES, VO_COMMERCIAL_TERMS } from '../lib/virtualOfficeTiers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatINR = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Lazy-load Razorpay checkout script
const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const VirtualOfficePreviewPage = () => {
  const { venueId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, api, isAuthenticated } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [buyingTier, setBuyingTier] = useState(null);   // tier id currently being purchased
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', message: '' });

  const sourceRef = searchParams.get('ref') || null;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/virtual-office/${venueId}`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [venueId]);

  const openEnquiry = (tierId = null) => {
    setSelectedTier(tierId);
    setEnquiryOpen(true);
  };

  const handleBuyNow = async (plan) => {
    if (!isAuthenticated) {
      toast.info('Please sign in, or continue as guest, to purchase');
      const back = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?redirect=${back}`);
      return;
    }
    setBuyingTier(plan.tier);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Failed to load payment gateway'); return; }
      // Build user's checkout payload
      const orderRes = await api.post('/virtual-office/checkout/order', {
        plan_id: plan.plan_id,
        company_name: user?.organization_name || user?.name || 'Self',
        contact_name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
      });
      const order = orderRes.data;

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: 'Clayworks',
        description: `${order.tier_name} — Annual Subscription`,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#c8a97e' },
        handler: async (response) => {
          try {
            await api.post('/virtual-office/checkout/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Subscription activated! Check your email for the invoice.');
            navigate('/dashboard');
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Payment verification failed');
          }
        },
        modal: { ondismiss: () => setBuyingTier(null) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally {
      setBuyingTier(null);
    }
  };

  const submitEnquiry = async () => {
    if (!form.company_name || !form.contact_name || !form.email || !form.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/virtual-office/enquiries`, {
        venue_id: venueId,
        plan_tier: selectedTier,
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        source_ref: sourceRef,
      });
      toast.success("Thanks! Our sales team will reach out within one business day.");
      setEnquiryOpen(false);
      setForm({ company_name: '', contact_name: '', email: '', phone: '', message: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (error || !data || !data.has_plans) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
            <h2 className="font-bold text-xl mb-2">No Virtual Office plans available</h2>
            <p className="text-muted-foreground text-sm mb-4">This centre doesn't have any active virtual office plans yet, or the link is invalid.</p>
            <Button onClick={() => navigate('/')}>Browse Other Centres</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { venue, plans, commercial_terms } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="vo-preview-page">
      {/* Top nav */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.webp" alt="Clayworks" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/login')}>Sign In</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-12 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
          <Badge variant="outline" className="mb-3 text-xs"><Sparkles className="w-3 h-3 mr-1" /> Virtual Office</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="vo-hero-title">
            A Premium Business Address<br />in <span className="text-primary">{venue.name}</span>
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
            Get GST registration support, mail handling, compliance documentation, and optional workspace access — without leasing a physical office.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" /> {venue.region || venue.address || 'Bangalore'}
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="px-4 pb-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {plans.map((p) => (
            <Card
              key={p.tier}
              className={`relative flex flex-col ${p.badge === 'Most Popular' ? 'border-primary border-2 shadow-lg' : p.badge === 'Premium' ? 'border-amber-400 border-2 shadow-lg' : ''}`}
              data-testid={`vo-card-${p.tier}`}
            >
              {p.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold ${p.badge === 'Premium' ? 'bg-amber-400 text-amber-950' : 'bg-primary text-primary-foreground'}`}>
                  <Star className="w-3 h-3 inline mr-1" /> {p.badge}
                </div>
              )}
              <CardContent className="p-6 flex-1 flex flex-col">
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-4">{p.tagline}</p>
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">{formatINR(p.monthly_price)}</span>
                    <span className="text-sm text-muted-foreground">/month</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Billed annually at <strong>{formatINR(p.annual_price)}</strong> · 12-month commitment · excl. GST
                  </p>
                </div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Best for</p>
                <p className="text-sm mb-4">{p.best_for}</p>

                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Includes</p>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {p.excludes?.length > 0 && (
                  <ul className="space-y-1 mb-4">
                    {p.excludes.map((f, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                        <XCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-col gap-2 mt-auto pt-3 border-t">
                  <Button onClick={() => handleBuyNow(p)} className="w-full" disabled={buyingTier === p.tier} data-testid={`vo-buy-${p.tier}`}>
                    {buyingTier === p.tier ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    {isAuthenticated ? 'Buy Now' : 'Sign in to Buy'} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button onClick={() => openEnquiry(p.tier)} variant="outline" size="sm" className="w-full" data-testid={`vo-enquire-${p.tier}`}>
                    Enquire instead
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="px-4 py-12 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-center mb-6">Quick Comparison</h2>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold">Feature</th>
                    {plans.map((p) => (
                      <th key={p.tier} className="text-center p-4 font-semibold whitespace-nowrap text-xs">{p.name.replace('Clayworks ', '')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {VO_COMPARISON_FEATURES.map((row, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="p-3 text-sm">{row.label}</td>
                      {plans.map((p) => {
                        const v = row.tiers[p.tier];
                        return (
                          <td key={p.tier} className="p-3 text-center">
                            {v === true ? <CheckCircle2 className="w-5 h-5 text-green-500 inline" /> :
                             v === false ? <XCircle className="w-4 h-4 text-red-400 inline opacity-50" /> :
                             <span className="text-xs font-medium">{v}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Commercial terms */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><FileCheck className="w-5 h-5 text-primary" /> Contract Terms</h3>
              <ul className="space-y-2 text-sm">
                {commercial_terms.contract_terms.map((t, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-primary mt-1">•</span><span>{t}</span></li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-primary" /> Compliance Requirements</h3>
              <ul className="space-y-2 text-sm">
                {commercial_terms.compliance.map((t, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-primary mt-1">•</span><span>{t}</span></li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-4 italic">{commercial_terms.note}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 py-12 bg-primary/5 border-t">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-bold mb-3">Ready to set up your business address?</h2>
          <p className="text-muted-foreground mb-6">Tell us about your needs and our sales team will reach out with a tailored quote.</p>
          <Button size="lg" onClick={() => openEnquiry(null)} data-testid="vo-enquire-bottom">
            <Mail className="w-4 h-4 mr-2" /> Enquire Now
          </Button>
        </div>
      </section>

      {/* Enquiry Modal */}
      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="max-w-md" data-testid="vo-enquiry-modal">
          <DialogHeader>
            <DialogTitle>Enquire About Virtual Office</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {selectedTier && (
              <div className="p-2.5 rounded bg-primary/5 border border-primary/20 text-sm">
                <span className="text-xs text-muted-foreground">Selected plan:</span>
                <p className="font-semibold">{plans.find((p) => p.tier === selectedTier)?.name}</p>
              </div>
            )}
            <div>
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="vo-form-company" />
            </div>
            <div>
              <Label>Your Name *</Label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="vo-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vo-form-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vo-form-phone" />
              </div>
            </div>
            <div>
              <Label>Message (optional)</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your requirements..." rows={3} data-testid="vo-form-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnquiryOpen(false)}>Cancel</Button>
            <Button onClick={submitEnquiry} disabled={submitting} data-testid="vo-form-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Submit Enquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VirtualOfficePreviewPage;
